import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Trash2, Database, Eraser, Rocket, AlertTriangle, ShieldAlert } from "lucide-react";

const SELECTIVE = [
  ["pecas", "Peças", "pecas"],
  ["maquinas", "Máquinas / Equipamentos", "maquinas"],
  ["usuarios", "Usuários de teste", "usuarios"],
  ["fornecedores", "Fornecedores", "fornecedores"],
  ["categorias", "Categorias", "categorias"],
  ["localizacoes", "Localizações", "localizacoes"],
  ["movimentacoes", "Movimentações (retiradas/entradas/devoluções/ajustes)", "movimentacoes"],
  ["inventarios", "Inventários", "inventarios"],
  ["alertas", "Alertas tratados", "alertas_tratados"],
  ["recomendacoes", "Recomendações de compra", "recomendacoes"],
  ["relacionamentos", "Relacionamentos Peça ↔ Máquina", "relacionamentos"],
  ["manutencao", "Peças em poder da manutenção", "pecas_em_manutencao"],
];

const OPERATIONAL_KEYS = ["movimentacoes", "inventarios", "alertas_tratados", "recomendacoes", "pecas_em_manutencao"];
const FIRSTUSE_KEYS = ["pecas", "maquinas", "usuarios", "fornecedores", "categorias", "localizacoes",
  "movimentacoes", "inventarios", "relacionamentos", "recomendacoes", "alertas_tratados", "pecas_em_manutencao"];

const LABELS = {
  pecas: "Peças", maquinas: "Máquinas", usuarios: "Usuários de teste", fornecedores: "Fornecedores",
  categorias: "Categorias", localizacoes: "Localizações", movimentacoes: "Movimentações", inventarios: "Inventários",
  itens_inventario: "Itens de inventário", alertas_tratados: "Alertas tratados", recomendacoes: "Recomendações",
  relacionamentos: "Relacionamentos Peça↔Máquina", maquinas_planejadas: "Máquinas planejadas",
  auditoria: "Registros de auditoria", pecas_em_manutencao: "Peças em poder da manutenção",
};

export function CleanupAdmin() {
  const [counts, setCounts] = useState(null);
  const [blockedTargets, setBlockedTargets] = useState([]);
  const [blockedModes, setBlockedModes] = useState(["operational", "first_use"]);
  const [forceEnabled, setForceEnabled] = useState(null); // null = carregando
  const [mode, setMode] = useState(null); // "operational" | "selective" | "first_use"
  const [targets, setTargets] = useState({});
  const [busy, setBusy] = useState(false);
  const [punctual, setPunctual] = useState(null);
  const [pbusy, setPbusy] = useState(false);
  const PUNCTUAL = [
    { target: "movimentacoes", label: "Limpar Movimentações", countKey: "movimentacoes", desc: "Apaga movimentações de teste permitidas; preserva ajustes vinculados a inventário." },
    { target: "manutencao", label: "Limpar Peças em Poder da Manutenção", countKey: "mov_manutencao", desc: "Remove as retiradas/devoluções vinculadas a máquinas (zera a tela)." },
    { target: "inventarios", label: "Limpar Inventários", countKey: "inventarios", desc: "Apaga inventários, contagens, divergências e ajustes de teste." },
  ];
  const runPunctual = async () => {
    setPbusy(true);
    try {
      const { data } = await api.post("/admin/cleanup-operational", { target: punctual.target });
      toast.success(`Limpeza concluída. ${data.total} registro(s) removido(s).`);
      setPunctual(null); loadPreview();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setPbusy(false); }
  };

  const loadPreview = () => api.get("/admin/cleanup/preview").then((r) => { setCounts(r.data.counts); setBlockedTargets(r.data.blocked_targets || []); setBlockedModes(r.data.blocked_modes || []); setForceEnabled(r.data.force_delete_enabled === true); }).catch(() => setForceEnabled(false));
  useEffect(() => { loadPreview(); }, []);

  const openMode = (m) => { setMode(m); if (m === "selective") setTargets({}); };

  const execute = async () => {
    setBusy(true);
    try {
      const body = mode === "selective" ? { mode, targets } : { mode };
      const { data } = await api.post("/admin/cleanup", body);
      toast.success(`Limpeza concluída. ${data.total} registro(s) removido(s). O sistema continua acessível.`);
      setMode(null);
      loadPreview();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };

  const selectedKeys = Object.keys(targets).filter((k) => targets[k]);
  const locked = forceEnabled !== true;
  const lockedTitle = "Modo de teste desligado: só o superadministrador pode ligar";
  const previewKeys = mode === "operational" ? OPERATIONAL_KEYS : mode === "first_use" ? FIRSTUSE_KEYS : [];

  return (
    <Card className="card-metal p-6 border-rose-900/40" data-testid="cleanup-admin-panel">
      <div className="flex items-center gap-2 mb-2"><ShieldAlert className="h-5 w-5 text-rose-400" /><h2 className="font-display text-xl font-semibold text-slate-100">Administração — Limpeza de Dados de Teste</h2></div>
      <div className="rounded-lg border border-amber-800/50 bg-amber-950/20 p-3 text-xs text-amber-200 mb-4 flex items-start gap-2">
        <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
        <span>Ferramenta de <b>teste</b>: só funciona com o modo de teste ligado pelo superadministrador e apaga <b>definitivamente</b> os dados desta empresa. Seu usuário, o administrador principal da empresa e as configurações essenciais são preservados. Registros antigos sem empresa definida nunca são apagados.</span>
      </div>

      {forceEnabled === false && (
        <div className="rounded-lg border border-sky-800/50 bg-sky-950/20 p-3 text-xs text-sky-200 mb-4 flex items-start gap-2" data-testid="cleanup-locked-notice">
          <ShieldAlert className="h-4 w-4 mt-0.5 shrink-0" />
          <span>As limpezas estão <b>desativadas</b> para esta empresa. Elas só ficam disponíveis quando o superadministrador liga o <b>modo de teste</b> no painel da plataforma.</span>
        </div>
      )}

      {counts && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4">
          {["pecas", "maquinas", "usuarios", "movimentacoes", "inventarios", "pecas_em_manutencao"].map((k) => (
            <div key={k} className="rounded-lg bg-slate-950/60 border border-slate-800 px-3 py-2">
              <div className="text-[11px] uppercase text-slate-500 font-mono">{LABELS[k]}</div>
              <div className="text-lg font-bold text-slate-100 font-mono" data-testid={`cleanup-count-${k}`}>{counts[k]}</div>
            </div>
          ))}
        </div>
      )}

      <div className="grid sm:grid-cols-3 gap-3">
        <Button data-testid="cleanup-btn-operational" disabled={locked || blockedModes.includes("operational")} title={locked ? lockedTitle : undefined} variant="secondary" onClick={() => openMode("operational")} className="h-auto py-3 flex-col gap-1 items-start text-left whitespace-normal bg-slate-800/50 hover:bg-slate-700/60">
          <span className="flex items-center gap-2 font-semibold text-amber-200"><Eraser className="h-4 w-4" /> Limpar Dados Operacionais</span>
          <span className="text-xs text-slate-400 font-normal whitespace-normal break-words">Apaga movimentações, inventários, alertas e recomendações. Mantém os cadastros.</span>
        </Button>
        <Button data-testid="cleanup-btn-selective" disabled={locked} title={locked ? lockedTitle : undefined} variant="secondary" onClick={() => openMode("selective")} className="h-auto py-3 flex-col gap-1 items-start text-left whitespace-normal bg-slate-800/50 hover:bg-slate-700/60">
          <span className="flex items-center gap-2 font-semibold text-sky-200"><Database className="h-4 w-4" /> Limpeza de Dados de Teste</span>
          <span className="text-xs text-slate-400 font-normal whitespace-normal break-words">Escolha exatamente quais tipos de dados remover.</span>
        </Button>
        <Button data-testid="cleanup-btn-firstuse" disabled={locked || blockedModes.includes("first_use")} title={locked ? lockedTitle : undefined} variant="secondary" onClick={() => openMode("first_use")} className="h-auto py-3 flex-col gap-1 items-start text-left whitespace-normal bg-rose-950/40 hover:bg-rose-900/50 border border-rose-800/50">
          <span className="flex items-center gap-2 font-semibold text-rose-200"><Rocket className="h-4 w-4" /> Preparar p/ Primeiro Uso</span>
          <span className="text-xs text-slate-400 font-normal whitespace-normal break-words">Apaga todos os cadastros e históricos desta empresa, menos seu usuário e o administrador principal.</span>
        </Button>
      </div>

      <div className="mt-4">
        <h3 className="text-sm font-semibold text-slate-300 mb-2">Limpeza operacional pontual</h3>
        <div className="grid sm:grid-cols-3 gap-3">
          {PUNCTUAL.map((p) => (
            <Button key={p.target} disabled={locked} title={locked ? lockedTitle : undefined} data-testid={`cleanup-op-btn-${p.target}`} variant="secondary" onClick={() => setPunctual(p)} className="h-auto py-3 flex-col gap-1 items-start text-left whitespace-normal bg-slate-800/50 hover:bg-slate-700/60">
              <span className="flex items-center gap-2 font-semibold text-slate-100"><Trash2 className="h-4 w-4 text-rose-400" /> {p.label}</span>
              <span className="text-xs text-slate-400 font-normal whitespace-normal break-words">{p.desc}</span>
              <span className="text-xs font-mono text-rose-300">{counts ? counts[p.countKey] : "—"} registro(s)</span>
            </Button>
          ))}
        </div>
      </div>

      <Dialog open={!!punctual} onOpenChange={(v) => !v && setPunctual(null)}>
        <DialogContent className="bg-slate-900 border-slate-700 text-slate-100" data-testid="cleanup-op-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl flex items-center gap-2"><Trash2 className="h-6 w-6 text-rose-400" /> {punctual?.label}</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-300" data-testid="cleanup-op-count"><b className="text-rose-300">{punctual && counts ? counts[punctual.countKey] : 0}</b> registro(s) serão removidos definitivamente. Esta ação não poderá ser desfeita.</p>
          <div className="rounded-lg border border-rose-800/60 bg-rose-950/30 p-3 text-xs text-rose-200 flex items-start gap-2"><AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" /><span>Os cadastros de peças, máquinas, usuários e fornecedores NÃO são afetados por esta limpeza.</span></div>
          <DialogFooter className="flex-col-reverse sm:flex-row gap-2">
            <Button variant="ghost" onClick={() => setPunctual(null)} data-testid="cleanup-op-cancel" className="w-full sm:w-auto">Cancelar</Button>
            <Button data-testid="cleanup-op-confirm" onClick={runPunctual} disabled={pbusy} className="w-full sm:w-auto bg-rose-700 hover:bg-rose-600">{pbusy ? "Limpando..." : "Confirmar limpeza"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!mode} onOpenChange={(v) => !v && setMode(null)}>
        <DialogContent className="bg-slate-900 border-slate-700 text-slate-100 max-h-[85vh] overflow-y-auto" data-testid="cleanup-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2">
              <Trash2 className="h-6 w-6 text-rose-400" />
              {mode === "operational" ? "Limpar Dados Operacionais de Teste" : mode === "first_use" ? "Preparar Sistema para Primeiro Uso" : "Limpeza de Dados de Teste"}
            </DialogTitle>
          </DialogHeader>

          {mode === "selective" ? (
            <div className="space-y-1.5">
              <p className="text-sm text-slate-400 mb-2">Selecione o que deseja remover. Registros dependentes serão removidos junto para não deixar órfãos.</p>
              {SELECTIVE.map(([key, label, countKey]) => (
                <label key={key} data-testid={`cleanup-target-${key}`} className="flex items-center justify-between gap-3 rounded-lg bg-slate-950/50 border border-slate-800 px-3 py-2 cursor-pointer">
                  <span className="flex items-center gap-2 text-sm">
                    <input type="checkbox" disabled={blockedTargets.includes(key)} checked={!!targets[key]} onChange={(e) => setTargets({ ...targets, [key]: e.target.checked })} className="h-4 w-4 accent-rose-500" />
                    {label}{blockedTargets.includes(key) && <span className="text-xs text-amber-300"> — protegido</span>}
                  </span>
                  <span className="font-mono text-xs text-slate-400">{counts ? counts[countKey] : "—"}</span>
                </label>
              ))}
            </div>
          ) : (
            <div className="space-y-1.5">
              <p className="text-sm text-slate-400 mb-2">Serão removidos os seguintes registros desta empresa:</p>
              {previewKeys.map((k) => (
                <div key={k} className="flex items-center justify-between rounded-lg bg-slate-950/50 border border-slate-800 px-3 py-2 text-sm">
                  <span>{LABELS[k]}</span><span className="font-mono text-xs text-rose-300">{counts ? counts[k] : "—"}</span>
                </div>
              ))}
            </div>
          )}

          <div className="rounded-lg border border-rose-800/60 bg-rose-950/30 p-3 text-xs text-rose-200 flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>Esta operação é <b>irreversível</b>.{mode === "first_use" && " Todos os cadastros e históricos desta empresa serão apagados."} Seu usuário, o administrador principal da empresa e as configurações essenciais são mantidos.</span>
          </div>

          <DialogFooter className="flex-col-reverse sm:flex-row gap-2">
            <Button variant="ghost" onClick={() => setMode(null)} data-testid="cleanup-cancel" className="w-full sm:w-auto">Cancelar</Button>
            <Button data-testid="cleanup-confirm" onClick={execute} disabled={busy || (mode === "selective" && selectedKeys.length === 0)} className="w-full sm:w-auto bg-rose-700 hover:bg-rose-600">
              {busy ? "Limpando..." : "Executar limpeza"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
